import { create } from 'zustand'
import { ViewWorker } from '@/lib/workers/viewWorker'
import { ServiceWorker } from '@/lib/workers/serviceWorker'
import { User } from '@/lib/api/client'
import { USER_GROUPS } from './constants'
import { ViewsInProgress, ViewsCompleted } from '@/lib/workers/types'
import { arcus, KEYS } from '@/lib/arcus'
import { toMB } from '@/utils'

const DATA_CAP = 50

interface CADPageState {
  sessionID?: string
  viewWorker?: ViewWorker | null
  serviceWorker?: ServiceWorker | null
  viewUploadsInProgress: ViewsInProgress[]
  viewUploadsCompleted: ViewsCompleted[]
  currentBatch: ViewsCompleted[]
  nBatches: number
  isViewUploaded: boolean
  isViewWorkerProcessing: boolean
  webworkerDebugger: {
    canvas: HTMLCanvasElement | null
    images: string[]
  }
  currentUser?: User | null
  showStatsPanel?: boolean
  onUserLogin: (user: User) => void
  onUserLogout: () => void
  initViewWorker: () => void
  initServiceWorker: () => void
  isUserReadOnly: () => boolean
  isUserSuperLooker: () => boolean
  setViewUploadsInProgress: (views: ViewsInProgress[]) => void
  setViewUploadsCompleted: (views: ViewsCompleted[]) => void
  setViewWorkerHasKicked: () => void
  setViewWorkerHasStarted: () => void
  addWebworkerDebuggerImage: (image: string) => void
  toggleStatsPanel?: () => void
}

export const useAppStore = create<CADPageState>((set, get) => ({
  sessionID: new Date(Date.now()).toLocaleString(),
  viewWorker: null,
  currentUser: null,
  viewUploadsInProgress: [],
  viewUploadsCompleted: [],
  isViewUploaded: false,
  isViewWorkerProcessing: false,
  currentBatch: [],
  nBatches: 0,
  webworkerDebugger: {
    canvas: null,
    images: [],
  },
  showStatsPanel: false,
  onUserLogin: (user: User) => {
    set({
      currentUser: user,
    })
  },
  onUserLogout: () => {
    set({
      currentUser: null,
    })
  },

  addWebworkerDebuggerImage: (image: string) =>
    set((state) => {
      return {
        webworkerDebugger: {
          ...state.webworkerDebugger,
          images: [...state.webworkerDebugger.images, image],
        },
      }
    }),

  isUserReadOnly: () =>
    get().currentUser?.groups.includes(USER_GROUPS.READ_ONLY) || false,
  isUserSuperLooker: () =>
    get().currentUser?.groups.includes(USER_GROUPS.SUPER_LOOKER) || false,
  initViewWorker: () => {
    set((state) => {
      if (state.viewWorker) {
        return {}
      }
      const viewWorker = new ViewWorker({
        onUploadsInProgress: state.setViewUploadsInProgress,
        onUploadsCompleted: state.setViewUploadsCompleted,
        onScreenshot: state.addWebworkerDebuggerImage,
        onKick: state.setViewWorkerHasKicked,
        onStart: state.setViewWorkerHasStarted,
      })
      return {
        viewWorker,
        webworkerDebugger: { canvas: viewWorker.canvas, images: [] },
      }
    })
  },

  initServiceWorker: () => {
    set((state) => {
      if (state.serviceWorker) {
        return {}
      }
      const serviceWorker = new ServiceWorker()
      return {
        serviceWorker,
      }
    })
  },

  toggleStatsPanel: () =>
    set((state) => ({ showStatsPanel: !state.showStatsPanel })),

  setViewWorkerHasKicked: () => set({ isViewWorkerProcessing: false }),

  setViewWorkerHasStarted: () => set({ isViewWorkerProcessing: true }),

  setViewUploadsInProgress: (views: ViewsInProgress[]) => {
    set((state) => {
      const seenViews: { [k: string]: ViewsInProgress } = {}

      state.viewUploadsInProgress.forEach((view) => {
        seenViews[view.viewJobId] = view
      })

      views.forEach((view) => {
        seenViews[view.viewJobId] = view
      })

      const uniqueViews = Object.values(seenViews)

      return {
        viewUploadsInProgress: uniqueViews,
        isViewUploaded: false,
      }
    })
  },
  setViewUploadsCompleted: (views: ViewsCompleted[]) =>
    set((state) => {
      const viewUploadsInProgress = state.viewUploadsInProgress.filter(
        ({ viewJobId }) => !views.some((view) => view.viewJobId === viewJobId),
      )

      const currentBatch = [...state.currentBatch, ...views]
      const batchCompleted = viewUploadsInProgress.length === 0

      // Log data for completed batch up to DATA_CAP batches
      if (
        batchCompleted &&
        currentBatch.length > 0 &&
        state.nBatches < DATA_CAP
      ) {
        const first = currentBatch[0]
        const last = currentBatch[currentBatch.length - 1]
        const viewCount = currentBatch.length
        const cadVersionId = first.cadVersionId || ''
        const runtime = ((last.endTime || 0) - (first.startTime || 0)) / 1000

        arcus.log(KEYS.MEMORY, { cadVersionId })
        arcus.log(KEYS.VIEW_BATCH, {
          runtime,
          viewCount,
          cadVersionId,
          timestamp: Date.now(),
          batchID: new Date(Date.now()).toLocaleString(),
          avgViewTime: viewCount > 0 ? runtime / viewCount : 0,
          totalMB: first.gltfBytes ? toMB(first.gltfBytes) : 0,
        })
      }

      return {
        isViewUploaded: true,
        viewUploadsInProgress,
        isViewWorkerProcessing: viewUploadsInProgress.length > 0,
        viewUploadsCompleted: [...state.viewUploadsCompleted, ...views],
        currentBatch: batchCompleted ? [] : currentBatch,
        ...(batchCompleted && { nBatches: state.nBatches + 1 }),
      }
    }),
}))
